import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import SimpleTable from '../../uikit/simple_table';
import Button from '../../uikit/button';
import useIsMobile from '../../utils/hooks/useIsMobile';
import CompleteLogisticModal from '../../components/Logistics/modals/CompleteLogistcModal';

const buildSelectedItem = (item) => ({
  ...item,
  logisticQuantityId: item.logisticQuantityPriceId,
  quantity: item.quantity,
  quantityCompleted: item.quantityCompleted,
  inventoryDetails: item.inventoryDetails,
  price: item.inventoryDetails?.currentPricePerUnit,
});

const LogisticPendingReceiveTable = ({
  data = [],
  isFirst,
  isLast,
  page,
  pageSize,
  fetchNextPage,
  fetchPreviousPage,
  onReceiveSuccess,
}) => {
  const isMobile = useIsMobile();
  const [modal, setModal] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const closeModal = () => {
    setModal(null);
    setSelectedItem(null);
  };

  const handleReceiveClick = (item) => {
    setSelectedItem(buildSelectedItem(item));
    setModal('complete');
  };

  const closeModalAndRefetchData = () => {
    closeModal();
    onReceiveSuccess?.();
  };

  /* ------------------ Data Transform ------------------ */

  const transformedData = useMemo(
    () =>
      data.map((item) => ({
        logisticId: item.logisticId ?? '-',
        category:
          item.inventoryDetails?.inventoryDetailsParent?.inventoryType
            ?.inventoryType || '-',
        subCategory:
          item.inventoryDetails?.inventoryDetailsParent?.inventoryParentName ||
          '-',
        item: item.inventoryDetails?.inventoryName || '-',
        measurementUnit: item.inventoryDetails?.measurementUnit || '',
        fromSite: item.fromWarehouse?.roomName || '-',
        toSite: item.toWarehouse?.roomName || '-',
        quantity: item.quantity || 0,
        quantityCompleted: item.quantityCompleted || 0,
        raw: item,
      })),
    [data]
  );

  /* ------------------ Desktop Columns ------------------ */

  const columns = useMemo(
    () => [
      {
        accessorKey: 'slNo',
        header: 'SL No',
        cell: ({ row }) => page * pageSize + row.index + 1,
        minSize: 40,
        maxSize: 40,
      },
      { accessorKey: 'logisticId', header: 'Logistic ID' },
      { accessorKey: 'category', header: 'Category' },
      { accessorKey: 'subCategory', header: 'Sub Category' },
      { accessorKey: 'item', header: 'Item' },
      { accessorKey: 'fromSite', header: 'From Site' },
      { accessorKey: 'toSite', header: 'To Site' },
      {
        accessorKey: 'quantity',
        header: 'Quantity',
        cell: (row) =>
          `${parseFloat(row.getValue() || 0).toLocaleString('en-IN')} ${row.row.original.measurementUnit}`,
      },
      {
        accessorKey: 'quantityCompleted',
        header: 'Received Qty',
        cell: (row) => parseFloat(row.getValue() || 0).toLocaleString('en-IN'),
      },
      {
        id: 'action',
        header: 'Action',
        cell: (row) => (
          <Button
            variant="contained"
            onClick={() => handleReceiveClick(row.row.original.raw)}
            className="bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-3 rounded-lg"
          >
            Receive
          </Button>
        ),
      },
    ],
    [page, pageSize]
  );

  /* ------------------ Mobile Columns ------------------ */

  const mobileColumns = useMemo(
    () => [
      {
        accessorKey: 'logisticId',
        header: 'Details',
        cell: (row) => {
          const item = row.row.original;
          return (
            <div className="space-y-1">
              <div className="font-medium">
                Logistic ID: {item.logisticId}
              </div>
              <div className="text-sm text-gray-600">
                Category: {item.category}
              </div>
              <div className="text-sm text-gray-600">
                Sub Category: {item.subCategory}
              </div>
              <div className="text-sm text-gray-600">Item: {item.item}</div>
              <div className="text-sm text-gray-600">
                From Site: {item.fromSite}
              </div>
              <div className="text-sm text-gray-600">
                To Site: {item.toSite}
              </div>
              <div className="text-sm text-gray-600">
                Quantity:{' '}
                {parseFloat(item.quantity || 0).toLocaleString('en-IN')}{' '}
                {item.measurementUnit}
              </div>
              <div className="text-sm text-gray-600">
                Received Qty:{' '}
                {parseFloat(item.quantityCompleted || 0).toLocaleString(
                  'en-IN'
                )}
              </div>
              <div className="pt-1">
                <Button
                  variant="contained"
                  onClick={() => handleReceiveClick(item.raw)}
                  className="bg-orange-600 hover:bg-orange-700 text-white text-sm py-2 px-3 rounded-lg"
                >
                  Receive
                </Button>
              </div>
            </div>
          );
        },
      },
    ],
    []
  );

  /* ------------------ Render ------------------ */
  return (
    <>
      <SimpleTable
        data={transformedData}
        columns={isMobile ? mobileColumns : columns}
        pageIndex={page}
        isFirst={isFirst}
        isLast={isLast}
        showColumnSelector={false}
        fetchNextPage={fetchNextPage}
        fetchPreviousPage={fetchPreviousPage}
        pageSize={pageSize}
      />
      {modal === 'complete' && (
        <CompleteLogisticModal
          show={modal === 'complete'}
          onClose={closeModal}
          selectedItem={selectedItem}
          workOrder={selectedItem}
          fromRoomId={selectedItem?.fromWarehouse?.roomId}
          toRoomId={selectedItem?.toWarehouse?.roomId}
          batchWise={selectedItem?.toWarehouse?.batchwise}
          onAction={closeModalAndRefetchData}
          fromMi={true}
        />
      )}
    </>
  );
};

LogisticPendingReceiveTable.propTypes = {
  data: PropTypes.array.isRequired,
  isFirst: PropTypes.bool.isRequired,
  isLast: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  fetchNextPage: PropTypes.func.isRequired,
  fetchPreviousPage: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  onReceiveSuccess: PropTypes.func,
};

export default LogisticPendingReceiveTable;
